import { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { PHONE_QUERY } from '@/utils/breakpoints'
import { useMediaQuery } from '@/utils/hooks/useMediaQuery'

import styles from './HowItWorks.module.scss'
import { HowRail } from './HowRail'
import { STEPS, type Step } from './howSteps'

/**
 * "How it works" — the path through the product, in the alternating layout the landing
 * already uses for `BreakdownRow`: the figure on one side, the number and the words on the other,
 * sides swapping row to row. Six steps, replacing the four scoring axes.
 *
 * Two of the six are worth marking so nobody later reads them as shipped:
 *
 *   05 SEQUENCES DO NOT EXIST YET. The engine has one touch endpoint, `POST /api/runs/:id/draft`,
 *   and no cadence, follow-up or sequence route anywhere. The client wants the path to show where
 *   it lands, so the step describes what is coming and carries a "soon" badge — the promise is
 *   never silent.
 *
 *   06 CLOSING THE DEAL IS NOT SOMETHING WE DO. It is here so the reader can see what follows 05,
 *   not as a claim that the product closes anything.
 *
 * The four scoring axes are not lost: they are the detail inside step 03.
 *
 * The list of steps is howSteps.ts, shared with the phone's rail (HowRail.tsx).
 *
 * Every string of the block's own copy is a `t()` key under `landing.how`, in en.json and ru.json.
 *
 * The text INSIDE the miniatures is still hardcoded English, and that is a known debt, not a
 * decision: they are pictures of the product's own screens, and most of their labels already exist
 * as `app.*` keys the app itself renders in both locales. Reusing those keys is the fix, and it has
 * to happen before this ships to a Russian visitor.
 */

/**
 * Reveal on scroll, once. `rootMargin` fires the row a little before its top edge arrives, so the
 * motion is finished by the time the reader's eye lands on it — an element that starts animating
 * only when it is already being read reads as lag, not as polish.
 *
 * It unobserves after the first entry: a row that re-animates every time it scrolls back into view
 * is the effect calling attention to itself instead of to the step.
 *
 * No observer, no problem: `shown` starts true when IntersectionObserver is missing, so the block
 * degrades to a plain visible list rather than to blank space.
 */
const useRevealed = () => {
  const ref = useRef<HTMLDivElement>(null)
  const [shown, setShown] = useState(() => typeof IntersectionObserver === 'undefined')

  useEffect(() => {
    const node = ref.current
    if (!node || typeof IntersectionObserver === 'undefined') return
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return
        setShown(true)
        observer.disconnect()
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.15 },
    )
    observer.observe(node)
    return () => observer.disconnect()
  }, [])

  return { ref, shown }
}

const StepRow = ({ step }: { step: Step }) => {
  const { ref, shown } = useRevealed()
  const { t } = useTranslation()
  /* Ключ собирается из step.key, поэтому проверка i18n его не видит — ветки перечислены в
     landing.how.steps: connect, filters, score, shortlist, sequence, close. */
  const line = (part: string) => t(`landing.how.steps.${step.key}.${part}`)

  return (
    <div ref={ref} className={shown ? styles.rowShown : styles.row}>
      <div className={styles.shot}>
        <div className={styles.frame}>
          <step.Visual playing={shown} />
        </div>
      </div>
      <div className={styles.body}>
        <span className={styles.n}>
          {step.n}
          {step.soon ? <span className={styles.soon}>{t('landing.how.soon')}</span> : null}
        </span>
        <h3 className={styles.title}>{line('title')}</h3>
        <p className={styles.text}>{line('text')}</p>
      </div>
    </div>
  )
}

/* На телефоне — рельс по шагу на слайд (HowRail.tsx), на всём остальном — ряды, как были. Выбор в
   JS, а не двумя копиями под CSS: шесть анимированных миниатюр дважды в DOM и два набора
   IntersectionObserver ради одного видимого. Первый рендер уже верный — useMediaQuery читает запрос
   синхронно, так что десктоп не видит рельса ни на кадр. */
export const HowItWorks = () => {
  const phone = useMediaQuery(PHONE_QUERY)
  if (phone) return <HowRail />

  return (
    <div className={styles.root}>
      {STEPS.map((step) => (
        <StepRow key={step.n} step={step} />
      ))}
    </div>
  )
}
